import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, X, Loader2, Check } from 'lucide-react';
import { API } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import Fuse from 'fuse.js';
import getUserAvatar from '../../../application/common/utils/getUserAvatar';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
  };
};

interface UserItem {
  id: string;
  name: string;
  title?: string;
  imageName?: string;
  imageType?: string;
  initials?: string;
  avatarFile?: string;
  status?: string;
}

interface AddMembersModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedUsers: UserItem[]) => void;
  existingMemberIds: string[];
  title?: string;
}

export function AddMembersModal({
  open,
  onClose,
  onSubmit,
  existingMemberIds,
  title = 'Add Members',
}: AddMembersModalProps) {
  const [users, setUsers] = useState<UserItem[]>([]);
  const [searchString, setSearchString] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUsers();
      setSelectedUsers([]);
      setSearchString('');
    }
  }, [open]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const myQuery = gql`
        query MyQuery($orgID: ID!, $searchString: String!, $nextToken: String) {
          searchUsers(
            filter: {
              name: { wildcard: $searchString }
              orgID: { eq: $orgID }
              status: { ne: "deleted" }
            }
            limit: 750
            sort: { direction: asc, field: name }
            nextToken: $nextToken
          ) {
            items {
              id
              name
              status
              imageName
              imageType
              initials
              title
            }
            nextToken
          }
        }
      `;

      let allUsers: any[] = [];
      let nextToken: string | null = null;

      do {
        const res: any = await API.graphql({
          query: myQuery,
          variables: {
            orgID: global.me.orgID,
            searchString: '*',
            nextToken,
          },
        });
        allUsers = [...allUsers, ...(res?.data?.searchUsers?.items || [])];
        nextToken = res?.data?.searchUsers?.nextToken;
      } while (nextToken);

      // Filter to active users and get avatars
      const activeUsers = allUsers.filter((u) => u.status === 'active');

      const usersWithAvatars = await Promise.all(
        activeUsers.map(async (u) => {
          const avatarFile = await getUserAvatar(
            u.imageName,
            u.imageType,
            true
          );
          return { ...u, avatarFile };
        })
      );

      // Filter out existing members
      const available = usersWithAvatars.filter(
        (u) => !existingMemberIds.includes(u.id)
      );

      setUsers(available);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fuse = useMemo(
    () =>
      new Fuse(users, {
        keys: ['name', 'title'],
        includeScore: true,
        threshold: 0.3,
      }),
    [users]
  );

  const filteredUsers = useMemo(() => {
    if (!searchString.trim()) {
      return users;
    }
    return fuse.search(searchString).map((r) => r.item);
  }, [users, searchString, fuse]);

  const toggleUser = useCallback((user: UserItem) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.id === user.id);
      if (exists) {
        return prev.filter((u) => u.id !== user.id);
      }
      return [...prev, user];
    });
  }, []);

  const isSelected = useCallback(
    (userId: string) => {
      return selectedUsers.some((u) => u.id === userId);
    },
    [selectedUsers]
  );

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      return;
    }
    setIsSubmitting(true);
    await onSubmit(selectedUsers);
    setIsSubmitting(false);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-background rounded-xl border border-border shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="size-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search people..."
              className="pl-10"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              autoFocus
            />
          </div>
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedUsers.map((user) => (
                <span
                  key={user.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary"
                >
                  {user.name}
                  <button
                    onClick={() => toggleUser(user)}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto px-2 py-2 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <p className="text-sm">No people found</p>
            </div>
          ) : (
            filteredUsers.map((user) => {
              const selected = isSelected(user.id);
              return (
                <button
                  key={user.id}
                  onClick={() => toggleUser(user)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left',
                    selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                  )}
                >
                  {/* Avatar */}
                  {user.avatarFile ? (
                    <img
                      src={user.avatarFile}
                      alt={user.name}
                      className="size-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-semibold text-primary">
                        {user.initials || user.name?.[0]?.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user.name}
                    </p>
                    {user.title && (
                      <p className="text-xs text-muted-foreground truncate">
                        {user.title}
                      </p>
                    )}
                  </div>

                  {/* Check indicator */}
                  <div
                    className={cn(
                      'size-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
                      selected ? 'bg-primary border-primary' : 'border-border'
                    )}
                  >
                    {selected && (
                      <Check className="size-3 text-primary-foreground" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedUsers.length} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedUsers.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              Add {selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
